'use client'

import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { IconChevronLeft, IconChevronRight } from '@repo/design-system-cf/components/icons'
import { tw } from 'utils/classnames'

const DEFAULT_GAP = 12

interface InfiniteCarouselProps {
  /** Number of unique items (before tripling). Used for scroll wrap calculation. */
  itemCount: number
  /** Gap between items in pixels. Defaults to 12. */
  gap?: number
  scrollLeftLabel: string
  scrollRightLabel: string
  /** Tailwind classes for vertical arrow positioning. Defaults to "top-1/2 -translate-y-1/2". */
  arrowTopClassName?: string
  children: React.ReactNode
}

/**
 * Renders children inside an infinite-scrolling carousel.
 *
 * Consumers are responsible for tripling their items (render 3×) and adding
 * `data-card` to each item so the carousel can measure item width.
 *
 * Starts at scroll position 0 (SSR-safe). On the first user scroll the
 * position is silently moved into the middle copy so the carousel loops
 * infinitely from that point on.
 */
export function InfiniteCarousel({
  itemCount,
  gap = DEFAULT_GAP,
  scrollLeftLabel,
  scrollRightLabel,
  arrowTopClassName = 'top-1/2 -translate-y-1/2',
  children,
}: InfiniteCarouselProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = React.useState(false)

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const handleScroll = () => {
      if (el.scrollLeft > gap) {
        setShowLeftArrow(true)
        el.removeEventListener('scroll', handleScroll)
      }
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [gap])

  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }

    const wrapToMiddle = () => {
      const setWidth = getSetWidth(el, itemCount, gap)
      if (setWidth === 0 || el.scrollLeft <= gap) {
        return
      }

      if (el.scrollLeft < setWidth) {
        el.style.scrollSnapType = 'none'
        el.scrollLeft += setWidth
        requestAnimationFrame(() => {
          el.style.scrollSnapType = ''
        })
      } else if (el.scrollLeft >= setWidth * 2) {
        el.style.scrollSnapType = 'none'
        el.scrollLeft -= setWidth
        requestAnimationFrame(() => {
          el.style.scrollSnapType = ''
        })
      }
    }

    // Wrap immediately when hitting the boundary so the user never sees the
    // hard edge. The browser's momentum scroll continues from the new position.
    const handleScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth
      const setWidth = getSetWidth(el, itemCount, gap)
      if (setWidth === 0) {
        return
      }

      if (el.scrollLeft >= maxScroll - 1) {
        el.style.scrollSnapType = 'none'
        el.scrollLeft -= setWidth
        requestAnimationFrame(() => {
          el.style.scrollSnapType = ''
        })
      } else if (el.scrollLeft <= 0 && showLeftArrow) {
        el.style.scrollSnapType = 'none'
        el.scrollLeft += setWidth
        requestAnimationFrame(() => {
          el.style.scrollSnapType = ''
        })
      }
    }

    // Safety net: wrap when the user stops scrolling somewhere in set 1 or 3.
    const handleScrollEnd = () => {
      wrapToMiddle()
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    el.addEventListener('scrollend', handleScrollEnd)
    return () => {
      el.removeEventListener('scroll', handleScroll)
      el.removeEventListener('scrollend', handleScrollEnd)
    }
  }, [itemCount, gap, showLeftArrow])

  const scrollByPage = React.useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current
      if (!el) {
        return
      }
      const firstCard = el.querySelector<HTMLElement>('[data-card]')
      if (!firstCard) {
        return
      }
      const itemWidth = firstCard.offsetWidth + gap
      const itemsPerPage = Math.max(1, Math.floor(el.clientWidth / itemWidth))
      el.scrollBy({
        left: direction === 'left' ? -(itemsPerPage * itemWidth) : itemsPerPage * itemWidth,
        behavior: 'smooth',
      })
    },
    [gap],
  )

  return (
    <div className="relative w-full">
      <div
        className={tw(
          'absolute left-0 top-0 h-full w-12 bg-gradient-to-r from-cf-grey-50 via-cf-grey-50/40 to-cf-grey-50/0 z-[1] pointer-events-none transition-opacity duration-300',
          showLeftArrow ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className="absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-cf-grey-50 via-cf-grey-50/40 to-cf-grey-50/0 z-[1] pointer-events-none" />
      <div
        className={tw(
          'absolute left-2 z-[3] transition-opacity duration-300',
          arrowTopClassName,
          showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <Button
          variant="primary"
          size="small"
          onClick={() => scrollByPage('left')}
          aria-label={scrollLeftLabel}
          iconLeft={<IconChevronLeft />}
        />
      </div>
      <div className={tw('absolute right-2 z-[3]', arrowTopClassName)}>
        <Button
          variant="primary"
          size="small"
          onClick={() => scrollByPage('right')}
          aria-label={scrollRightLabel}
          iconLeft={<IconChevronRight />}
        />
      </div>
      <div
        ref={scrollRef}
        className="flex overflow-x-auto w-full scroller-x-xs [--scroller-size:0px] snap-x snap-mandatory"
        style={{ gap }}
      >
        {children}
      </div>
    </div>
  )
}

function getSetWidth(container: HTMLElement, itemCount: number, gap: number): number {
  const firstCard = container.querySelector<HTMLElement>('[data-card]')
  if (!firstCard) {
    return 0
  }
  return (firstCard.offsetWidth + gap) * itemCount
}
